import Card from "components/Card";
import Button from "components/Button";
import CheckedComponent from "components/CheckedComponent";
import Badge from "components/Badge";
import { Tooltip } from "components/Tooltip";
import Toaster from "components/Toaster";
import { fmtNumber } from "utils";
import { ReactComponent as BillingV2Icon } from "assets/icons/billing_v2_icon.svg";
import { useUser } from "routes/dataroutes/UserData";
import { useBillingFlow } from "../providers/BillingFlowProvider";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useNextHigherPlan } from "v2/hooks/billing/pricing-options/useNextHigherPlan";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { getMonthlyPriceInUSD, getMonthlyCreditsEquivalent, isEnterprise, formatRegionLabel, getPlanTier } from "utils/planUtils";
import { PLAN_TIER_FEATURES } from "constants/planTierFeatures";
import { formatSubscriptionPrice, calculateBillingPeriod } from "../tabs/billingDataTransformers";
import Messages from "misc/Messages";
import { useBillingNavigation } from "../hooks/useBillingNavigation";
import LoadingCard from "../components/LoadingCard";
import RecommendedBadge from "../components/RecommendedBadge";
import {
  trackBillingV2UpgradeClicked,
  trackBillingV2ViewAllPlansClicked,
  trackBillingV2ContactUsClicked,
} from "utils/Tracking";


// Enterprise upgrade card for SLG users
function EnterpriseUpgradeCard() {
  const { navigate } = useBillingNavigation();

  const handleContactUsClick = () => {
    trackBillingV2ContactUsClicked('upgrade_card_enterprise');
    navigate("plans/contact-sales", { viewTransition: true });
  };

  return (
    <Card
      size="XL"
      className="shadow-md !border-primary-200 !bg-[linear-gradient(to_right,#F5F3FF_0%,#FFF_100%)]"
    >
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-semibold leading-8 text-gray-900">
          Upgrade to Enterprise!
        </h3>
        <p className="text-sm text-gray-600">
          Are you not sure what plan fits best for you? Or do you want to discuss the option of a custom plan?
        </p>
      </div>

      {/* Let's talk */}
      <div className="flex flex-col gap-1 mt-6">
        <div className="text-4xl font-semibold text-gray-900">
          Let's talk
        </div>
        <p className="text-sm text-gray-600">
          / custom pricing
        </p>
      </div>

      {/* Enterprise features - 2 column grid, gap-x matches PlanFeaturesCard for alignment */}
      <div className="grid grid-cols-2 gap-x-[72px] gap-y-3 mt-6">
        {Messages.billingV2.enterprise.features.map((feature, index) => (
          <CheckedComponent
            key={index}
            variant="outline"
            className="text-sm font-medium text-gray-900"
          >
            <span className="inline-flex items-center gap-2">
              {feature.infoTooltip ? (
                <Tooltip content={feature.infoTooltip}>
                  {feature.label}
                </Tooltip>
              ) : (
                feature.label
              )}
              {feature.badge === "new" && <Badge textColorClass="text-gray-900" />}
            </span>
          </CheckedComponent>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-4">
        <Button
          text="CONTACT US"
          className="button button-primary"
          size="SM"
          onClick={handleContactUsClick}
        />
      </div>
    </Card>
  );
}

export default function UpgradeCard() {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const { nextHigherPlan, isLoading } = useNextHigherPlan();
  const { navigate } = useBillingNavigation();
  const { flowType } = useBillingFlow();
  const { pricingOptions } = usePricingOptions();
  const autoUpgradeEnabled = pricingOptions?.["auto-upgrade"]?.enabled ?? false;
  // Check if user is already on an enterprise plan
  const isEnterprisePlan = isEnterprise(activeSubscription?.planSlug);

  // Don't show upgrade card if user is already on enterprise
  if (isEnterprisePlan) {
    return null;
  }

  // SLG users (not on enterprise yet) see the enterprise upgrade card
  if (flowType === 'slg') {
    return (
      <LoadingCard isLoading={isLoading}>
        <EnterpriseUpgradeCard />
      </LoadingCard>
    );
  }

  // If there's no next higher plan (top-tier PLG users), show enterprise card
  if (!nextHigherPlan) {
    return (
      <LoadingCard isLoading={isLoading}>
        <EnterpriseUpgradeCard />
      </LoadingCard>
    );
  }

  const nextPlanPrice = formatSubscriptionPrice(
    nextHigherPlan.price,
    nextHigherPlan.currencyCode
  );

  const nextPlanBillingPeriod = calculateBillingPeriod(
    nextHigherPlan.billingPeriod,
    nextHigherPlan.billingPeriodUnit
  );

  // Prefer activeSubscription for current-plan limits — it's fresh from Chargebee on every
  // mutation, while user.* lags behind until the webhook updates the DB. Free users have no
  // activeSubscription, so user.* is the natural fallback.
  const currentCreditLimit = activeSubscription
    ? getMonthlyCreditsEquivalent(activeSubscription)
    : 0;
  const currentConcurrencyLimit = activeSubscription?.apiConcurrencyLimit;

  const nextCreditLimit = getMonthlyCreditsEquivalent(nextHigherPlan);

  const capacityMultiplier =
    nextCreditLimit && currentCreditLimit
      ? Math.round(nextCreditLimit / currentCreditLimit)
      : null;

  // Calculate daily cost difference using monthly USD prices
  const currentMonthlyUSD = activeSubscription ? getMonthlyPriceInUSD(activeSubscription) : 0;
  const nextMonthlyUSD = getMonthlyPriceInUSD(nextHigherPlan);

  const dailyCostDiff =
    nextMonthlyUSD > 0
      ? ((nextMonthlyUSD - currentMonthlyUSD) / 30).toFixed(2)
      : null;

  // Feature delta — only surface what the current plan doesn't already carry.
  // Same-tier upgrades show no extra bullets; the quantitative credits/threads deltas
  // above carry the story.
  // TODO falling back temporarily to the "free" plan... should not make that the default, should better handle the cases
  //  when the user does not have an active subscription (like it's banned from free or blocked otherwise)
  const currentPlanSlug = activeSubscription?.planSlug || "free";
  const currentTierFeatures = PLAN_TIER_FEATURES[getPlanTier(currentPlanSlug)] || [];
  const targetTierFeatures = PLAN_TIER_FEATURES[getPlanTier(nextHigherPlan.planSlug)] || [];
  const newTierFeatures = targetTierFeatures.filter(
    tf => !currentTierFeatures.some(cf => cf.label === tf.label)
  );

  // Geo row shows when the user is on a free-tier flow (so the paid plan's geo
  // entitlement is always surfaced on free → paid upgrades, even if backend values
  // happen to match) or when the two plans genuinely differ. Using `flowType` from
  // useBillingFlow keeps the "free-tier" definition in sync with the rest of the app
  // (covers free, standard_extended_trial, mini family, and affiliate variants).
  const isFreeCurrent = flowType === "free";
  const currentGeo = activeSubscription?.apiGeotargeting;
  const targetGeo = nextHigherPlan.apiGeotargeting;
  const showGeoRow = isFreeCurrent || currentGeo !== targetGeo;

  const isAlreadyScheduled = activeSubscription?.scheduledSubscription?.planSlug === nextHigherPlan.planSlug;
  const isBlockedNonBanned = user?.isBlocked && user?.blockingCode !== "banned_from_free";

  const handleUpgradeClick = () => {
    // When auto-upgrade is enabled, navigate to disable flow (matches billing v1)
    if (autoUpgradeEnabled) {
      navigate("plans/auto-upgrade/disable", { viewTransition: true });
      return;
    }

    if (user?.hasPaymentIssues) {
      Toaster.showToast({
        toastId: "payment-issues-upgrade",
        type: "error",
        title: "Outstanding invoices",
        description: "Please resolve your outstanding invoices before upgrading."
      });
      navigate("../details");
      return;
    }
    trackBillingV2UpgradeClicked({
      source: 'upgrade_card',
      current_plan: currentPlanSlug,
      target_plan: nextHigherPlan.planSlug,
    });
    navigate(`plans/upgrade/${nextHigherPlan.planSlug}`);
  };

  const handleViewAllPlansClick = () => {
    trackBillingV2ViewAllPlansClicked('upgrade_card');
    navigate("plans", { viewTransition: true });
  };

  return (
    <LoadingCard isLoading={isLoading}>
      <Card
        size="XL"
        className="shadow-md !border-primary-200 !bg-[linear-gradient(to_right,#F5F3FF_0%,#FFF_100%)] relative"
      >
      {/* Recommended badge */}
      <RecommendedBadge className="absolute top-6 right-6" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left side - content */}
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col gap-2">
            <h3 className="text-2xl font-semibold leading-8 text-gray-900">
              Upgrade to the {nextHigherPlan.planName} Plan
            </h3>
            <p className="text-sm text-gray-600">
              {flowType === 'free' && capacityMultiplier
                ? `Get ${capacityMultiplier}x more credits with the ${nextHigherPlan.planName} plan`
                : capacityMultiplier && dailyCostDiff
                ? `${capacityMultiplier}X your credits for just $${dailyCostDiff} more per day`
                : dailyCostDiff
                ? `$${dailyCostDiff} more per day`
                : `Upgrade to the next tier for more capacity`}
            </p>
          </div>

          {/* Price */}
          {nextPlanPrice && (
            <div className="text-4xl font-semibold text-gray-900">
              {nextPlanPrice}
              <span className="text-sm font-normal text-gray-600">
                /{nextPlanBillingPeriod}
              </span>
            </div>
          )}

          {/* Features */}
          <div className="flex flex-col gap-3">
            <CheckedComponent
              variant="outline"
              className="text-base font-medium text-gray-900"
            >
              {fmtNumber(nextCreditLimit)} API Credits
              {currentCreditLimit &&
                ` (Up from ${fmtNumber(currentCreditLimit)})`}
            </CheckedComponent>
            <CheckedComponent
              variant="outline"
              className="text-base font-medium text-gray-900"
            >
              {nextHigherPlan.apiConcurrencyLimit} Concurrent Threads
              {currentConcurrencyLimit &&
                ` (Up from ${currentConcurrencyLimit})`}
            </CheckedComponent>
            {showGeoRow && (
              <CheckedComponent
                variant="outline"
                className="text-base font-medium text-gray-900"
              >
                {formatRegionLabel(targetGeo)}
              </CheckedComponent>
            )}
            {newTierFeatures.map((feature) => (
              <CheckedComponent
                key={feature.label}
                variant="outline"
                className="text-base font-medium text-gray-900"
              >
                <span className="inline-flex items-center gap-2">
                  {feature.infoTooltip ? (
                    <Tooltip content={feature.infoTooltip}>
                      {feature.label}
                    </Tooltip>
                  ) : (
                    feature.label
                  )}
                  {feature.badge === "new" && <Badge textColorClass="text-gray-900" />}
                </span>
              </CheckedComponent>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              text={autoUpgradeEnabled ? "DISABLE AUTO-UPGRADE" : isAlreadyScheduled ? "CHANGE SCHEDULED" : `UPGRADE TO ${nextHigherPlan.planName.toUpperCase()}`}
              className="button button-primary"
              size="SM"
              onClick={handleUpgradeClick}
              disabled={(isAlreadyScheduled && !autoUpgradeEnabled) || isBlockedNonBanned}
            />
            <Button
              text="VIEW ALL PLANS"
              className="button button-secondary"
              size="SM"
              onClick={handleViewAllPlansClick}
              disabled={isBlockedNonBanned}
            />
          </div>
        </div>
        {/* Illustration - hidden on mobile, shown on desktop */}
        <div className="hidden lg:flex items-center justify-center">
          <BillingV2Icon className="w-60 h-60 flex-shrink-0" />
        </div>
      </div>
      </Card>
    </LoadingCard>
  );
}
